import type { DragEvent, KeyboardEvent, MouseEvent } from 'react';
import { cn } from '@immo/ui-kit';
import type { FleetBoardCard } from '@immo/api-contract';
import { ExternalLinkIcon, GripIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { formatDuration } from '@/lib/landiq/format-duration';
import { CardTag, UNTAKEN_TAGS } from '@/components/fleet/board/card-tag';

/** Where a dragged card would land on this one: in the slot above it, or below it. */
export type DropSide = 'above' | 'below';

/**
 * A queued card the operator may move, the browser's own drag and drop as on the LandIQ board: the card is dragged,
 * every queued card of the lane is a drop target, and the grip moves it with the arrow keys.
 */
export type BoardCardDrag = {
  /** This card is the one being dragged: it stays mounted as a dashed placeholder, or `dragend` never fires. */
  readonly dragging: boolean;
  /** A dragged card hovers this one: the line where it would land. */
  readonly dropSide: DropSide | null;
  /** A move of this lane is in flight: nothing moves until it settles. */
  readonly locked: boolean;
  /** A card of this lane's queue is being dragged: only then is this one a drop target. */
  readonly accepting: boolean;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
  readonly onDragOver: (side: DropSide) => void;
  readonly onDrop: (side: DropSide) => void;
  /** ArrowUp / ArrowDown on the grip. */
  readonly onStep: (by: -1 | 1) => void;
};

/** The half of the card the pointer is over. */
function sideOf(event: DragEvent<HTMLElement>): DropSide {
  const box = event.currentTarget.getBoundingClientRect();

  return event.clientY < box.top + box.height / 2 ? 'above' : 'below';
}

type Props = {
  readonly card: FleetBoardCard;
  /** The moment "how long" is measured to: the page's render time. */
  readonly now: number;
  /** The all-projects view names each card's project. */
  readonly showProject?: boolean;
  /** Its place in the lane's queue, from 1. */
  readonly position?: number;
  /** The first of the queue: what the lane's next `jobs/next` hands out. */
  readonly next?: boolean;
  /** Opens the ticket's details. A click on the Linear link or the drag handle never calls it. */
  readonly onOpen?: ((card: FleetBoardCard) => void) | undefined;
  /** Present only on a card the operator may drag: it renders the handle. */
  readonly drag?: BoardCardDrag | undefined;
};

const DETAIL_TONE: Partial<Record<FleetBoardCard['tag'], string>> = {
  silent: 'text-danger-700',
  stranded: 'text-warning-700',
  parked: 'text-danger-700',
  capped: 'text-warning-700',
  answered: 'text-success-700',
};

/**
 * One ticket: its id links to Linear, a click anywhere else (or Enter on its title) opens its details, and a queued
 * card has a handle to drag it by.
 */
export function BoardCard({ card, now, showProject = false, position, next = false, onOpen, drag }: Props) {
  const t = useTranslations('pages.admin.fleet-board');
  const { tag, job, history } = card;
  const untaken = UNTAKEN_TAGS.includes(tag);
  const since = (from: string | null) => (from === null ? null : formatDuration(now - Date.parse(from)));

  const waiting = since(card.stateEnteredAt);
  const lead =
    job !== null
      ? `${job.worker} · ${t('since', { duration: since(job.startedAt ?? job.claimedAt) ?? '' })}`
      : tag === 'stranded'
        ? null
        : waiting === null
          ? card.state
          : `${card.state} · ${t('waiting', { duration: waiting })}`;

  const beat = since(job?.lastBeatAt ?? job?.startedAt ?? job?.claimedAt ?? null);
  const reason =
    tag === 'running' || tag === 'silent'
      ? beat === null
        ? null
        : t('beat', { duration: beat })
      : tag === 'stranded'
        ? t('stranded', { state: card.state })
        : tag === 'bound'
          ? t('bound', { worker: card.boundTo ?? '' })
          : tag === 'parked'
            ? card.reason
            : tag === 'assisted'
              ? t('assisted')
              : tag === 'waiting'
                ? card.waitingOn.length > 0
                  ? t('waiting-on', { tickets: card.waitingOn.join(', ') })
                  : t('waiting-on-unknown')
                : tag === 'capped'
                  ? t('capped', { runs: history.runs, limit: history.limit })
                  : tag === 'answered'
                    ? t('answered')
                    : null;
  const rounds =
    history.runs > 0 && tag !== 'capped'
      ? [
          t('history-runs', { count: history.runs }),
          history.parked > 0 ? t('history-parked', { count: history.parked }) : null,
          history.resumed > 0 ? t('history-resumed', { count: history.resumed }) : null,
        ]
      : [];
  const detail = [reason, ...rounds].filter((part) => part !== null && part !== '').join(' · ');

  const open = (event: MouseEvent) => {
    if (onOpen === undefined || (event.target as Element).closest('a, [data-drag-handle]') !== null) return;

    onOpen(card);
  };

  const movable = drag !== undefined && !drag.locked;
  const step = (event: KeyboardEvent) => {
    if (!movable || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;

    event.preventDefault();
    drag.onStep(event.key === 'ArrowUp' ? -1 : 1);
  };

  return (
    <li
      className="relative"
      onDragOver={
        drag === undefined || !drag.accepting
          ? undefined
          : (event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              drag.onDragOver(sideOf(event));
            }
      }
      onDrop={
        drag === undefined || !drag.accepting
          ? undefined
          : (event) => {
              event.preventDefault();
              drag.onDrop(sideOf(event));
            }
      }
    >
      {drag?.dropSide != null && (
        <span
          aria-hidden
          data-drop-line={drag.dropSide}
          className={cn(
            'pointer-events-none absolute inset-x-0 h-0.5 rounded-pill bg-interactive',
            drag.dropSide === 'above' ? '-top-[5px]' : '-bottom-[5px]',
          )}
        />
      )}
      <article
        aria-label={card.identifier}
        data-next={next ? 'true' : undefined}
        draggable={movable ? true : undefined}
        onDragStart={
          movable
            ? (event) => {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', card.identifier);
                // After the browser has taken its drag image of the real card.
                setTimeout(drag.onDragStart, 0);
              }
            : undefined
        }
        // Bound even while locked: a drop sets the lane busy before the browser fires `dragend`.
        onDragEnd={drag?.onDragEnd}
        onClick={open}
        className={cn(
          'relative flex min-w-0 flex-col gap-2 rounded-lg border border-solid p-3 shadow-sm',
          onOpen !== undefined && 'cursor-pointer hover:shadow-md',
          movable && 'cursor-grab',
          drag?.dragging && 'border-dashed border-neutral-300 bg-canvas shadow-none [&>*]:invisible',
          next ? 'border-interactive' : 'border-border',
          untaken ? 'bg-neutral-50' : 'bg-surface',
          tag === 'silent' && 'border-l-[3px] border-l-danger-600',
          tag === 'stranded' && 'border-l-[3px] border-l-warning-700',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {drag !== undefined && (
            <button
              type="button"
              data-drag-handle={card.identifier}
              aria-label={t('drag-handle', { ticket: card.identifier })}
              aria-description={t('dnd.instructions')}
              aria-disabled={drag.locked ? true : undefined}
              title={t('dnd.instructions')}
              onKeyDown={step}
              className="-ml-1 flex h-6 w-5 flex-none cursor-grab items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg active:cursor-grabbing"
            >
              <GripIcon aria-hidden className="h-4 w-4" />
            </button>
          )}
          {position !== undefined && (
            <span className={cn('font-mono text-xs font-semibold', next ? 'text-interactive' : 'text-text-secondary')}>
              #{position}
            </span>
          )}
          <a
            href={card.url}
            target="_blank"
            rel="noopener noreferrer"
            draggable={movable ? false : undefined}
            aria-label={t('linear-label', { ticket: card.identifier })}
            title={t('linear')}
            className="inline-flex w-fit items-center gap-1 font-mono text-sm text-text-tertiary hover:text-text-primary"
          >
            {card.identifier}
            <ExternalLinkIcon aria-hidden className="h-3 w-3" />
          </a>
          {showProject && (
            <span className="inline-flex h-[18px] items-center rounded-sm bg-neutral-100 px-1.5 font-mono text-xs text-text-secondary">
              {card.project}
            </span>
          )}
          <span className="flex-1" />
          {card.rank !== null && (
            <span title={t('manual-title')} className="text-xs italic text-text-tertiary">
              {t('manual')}
            </span>
          )}
          {card.priority !== 0 && (
            <span className="text-xs text-text-tertiary">{t(`priority.${String(card.priority)}`)}</span>
          )}
        </div>
        {/* The title is the keyboard way in: Enter or Space click it, and the click reaches the article's handler. */}
        {onOpen === undefined ? (
          <p
            title={card.title}
            className={cn(
              'min-w-0 break-words text-sm font-medium line-clamp-2',
              untaken ? 'text-text-secondary' : 'text-text-primary',
            )}
          >
            {card.title}
          </p>
        ) : (
          <button
            type="button"
            title={card.title}
            aria-label={t('open-ticket', { ticket: card.identifier })}
            className={cn(
              'min-w-0 break-words text-left text-sm font-medium line-clamp-2',
              untaken ? 'text-text-secondary' : 'text-text-primary',
            )}
          >
            {card.title}
          </button>
        )}
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex-none">
            <CardTag tag={tag} />
          </span>
          {lead !== null && (
            <p
              className="min-w-0 truncate text-xs text-text-tertiary"
              title={job === null ? undefined : t('running-on', { machine: job.machine })}
            >
              {lead}
            </p>
          )}
        </div>
        {detail !== '' && (
          <p title={detail} className={cn('min-w-0 truncate text-xs', DETAIL_TONE[tag] ?? 'text-text-tertiary')}>
            {detail}
          </p>
        )}
      </article>
    </li>
  );
}
